import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import GradeIcon from '@mui/icons-material/Grade';
import EditIcon from '@mui/icons-material/Edit';
import QuizQuestionEditor from './QuizQuestionEditor';
import QuizLinkGenerator from './QuizLinkGenerator';
import QuizResponseGrader from './QuizResponseGrader';
import {
  getAllQuizzes,
  createQuiz,
  deleteQuiz,
  getQuizLinksByQuizId,
  type Quiz,
  type QuizLink
} from '../../services/quizService';

type ViewMode = 'list' | 'questions' | 'links' | 'grader';

const QuizzesAdmin: React.FC = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [newQuizDescription, setNewQuizDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);

  useEffect(() => {
    loadQuizzes();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const data = await getAllQuizzes();
      setQuizzes(data);
      setError(null);
    } catch (err) {
      console.error('Error loading quizzes:', err);
      setError('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuiz = async () => {
    if (!newQuizTitle.trim()) {
      setError('Quiz title is required');
      return;
    }

    try {
      setCreating(true);
      await createQuiz(newQuizTitle, newQuizDescription || undefined);
      setNewQuizTitle('');
      setNewQuizDescription('');
      setCreateDialogOpen(false);
      await loadQuizzes();
      setError(null);
    } catch (err) {
      console.error('Error creating quiz:', err);
      setError('Failed to create quiz');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteQuiz = async (quizId: string) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This will also delete all questions, links, and responses.')) {
      return;
    }

    try {
      await deleteQuiz(quizId);
      await loadQuizzes();
      setError(null);
    } catch (err) {
      console.error('Error deleting quiz:', err);
      setError('Failed to delete quiz');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedQuizId(null);
    loadQuizzes(); // Reload to get fresh data
  };

  const handleEditQuestions = (quizId: string) => {
    setSelectedQuizId(quizId);
    setViewMode('questions');
  };

  const handleGenerateLinks = (quizId: string) => {
    setSelectedQuizId(quizId);
    setViewMode('links');
  };

  const handleGradeResponses = (quizId: string) => {
    setSelectedQuizId(quizId);
    setViewMode('grader');
  };

  // Render different views based on mode
  if (viewMode === 'questions' && selectedQuizId) {
    return <QuizQuestionEditor quizId={selectedQuizId} onBack={handleBackToList} />;
  }

  if (viewMode === 'links' && selectedQuizId) {
    return <QuizLinkGenerator quizId={selectedQuizId} onBack={handleBackToList} />;
  }

  if (viewMode === 'grader' && selectedQuizId) {
    return <QuizResponseGrader quizId={selectedQuizId} onBack={handleBackToList} />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Quizzes Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Quiz
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : quizzes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No quizzes yet. Create one to get started!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              quizzes.map((quiz) => (
                <TableRow key={quiz.id}>
                  <TableCell>{quiz.title}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {quiz.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(quiz.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Questions">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditQuestions(quiz.id)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate Links">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleGenerateLinks(quiz.id)}
                      >
                        <LinkIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Grade Responses">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleGradeResponses(quiz.id)}
                      >
                        <GradeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Quiz">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteQuiz(quiz.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {/* Create Quiz Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Quiz</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Quiz Title"
            type="text"
            fullWidth
            variant="outlined"
            value={newQuizTitle}
            onChange={(e) => setNewQuizTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newQuizDescription}
            onChange={(e) => setNewQuizDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreateQuiz} variant="contained" disabled={creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizzesAdmin;
